<template>
  <div :class="appShellClass">
    <TopBar v-if="session.isAuthenticated" />
    <div class="app-body">
      <KnowledgeBaseSidebar v-if="showDocTree" />
      <main class="app-main">
        <RouterView />
      </main>
    </div>
    <ApplicationUpdateBubble />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, provide, ref, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import type { EntityId } from './api/entityId';
import KnowledgeBaseSidebar from './components/KnowledgeBaseSidebar.vue';
import ApplicationUpdateBubble from './components/ApplicationUpdateBubble.vue';
import TopBar from './components/TopBar.vue';
import { sidebarControlsKey } from './sidebarControls';
import { useSessionStore } from './stores/session';
import { useApplicationUpdateStore } from './stores/applicationUpdate';
import { useNotificationsStore } from './stores/notifications';
import { useWorkspaceStore } from './stores/workspace';
import {
  startWorkspaceRealtime,
  stopWorkspaceRealtime,
  type WorkspaceRealtimeHandlers
} from './stores/workspaceRealtime';

const route = useRoute();
const session = useSessionStore();
const applicationUpdate = useApplicationUpdateStore();
const notifications = useNotificationsStore();
const workspace = useWorkspaceStore();
const sidebarHidden = ref(false);
const sidebarOpen = ref(false);

const inSettings = computed(() => route.meta.chrome === 'settings');
const showDocTree = computed(() => session.isAuthenticated && route.meta.chrome == null);
const workspaceActive = computed(() => session.isAuthenticated
  && (route.path === '/' || route.name === 'workspace-document'));
let realtimeErrorMessage: string | null = null;

watch(workspaceActive, active => applicationUpdate.setWorkspaceActive(active), { immediate: true });

const realtimeHandlers: WorkspaceRealtimeHandlers = {
  applicationVersion: message => applicationUpdate.observeServerVersion(message.version),
  documentCommentCreated: message => {
    workspace.applyDocumentCommentCreated(message);
    if (String(message.authorUserId) !== String(session.profile?.id)) {
      void notifications.loadUnread();
    }
  },
  documentPresenceSnapshot: message => workspace.applyDocumentPresenceSnapshot(message),
  documentPresenceChanged: message => workspace.applyDocumentPresenceChanged(message),
  documentContentSaved: message => workspace.applyDocumentContentSaved(message),
  workspaceTreeChanged: message => {
    void workspace.applyWorkspaceTreeChanged(message);
  },
  connected: () => {
    if (workspace.error === realtimeErrorMessage) {
      workspace.error = null;
    }

    realtimeErrorMessage = null;
  },
  reconnected: () => {
    workspace.refreshDocumentCommentsAfterReconnect();
    void notifications.loadUnread();
  },
  getOpenDocumentIds: () => workspace.getRealtimeOpenDocumentIds(),
  onError: message => {
    realtimeErrorMessage = message;
    workspace.error = message;
  }
};

let realtimeLifecycleVersion = 0;
let pendingRealtimeStop: Promise<void> = Promise.resolve();

watch(
  () => session.profile?.id,
  (userId, previousUserId) => {
    const version = ++realtimeLifecycleVersion;
    if (userId == null) {
      notifications.reset();
      workspace.resetRealtimeState();
      pendingRealtimeStop = stopWorkspaceRealtime();
      return;
    }

    if (previousUserId != null && String(previousUserId) !== String(userId)) {
      pendingRealtimeStop = stopWorkspaceRealtime();
    }

    notifications.reset();
    void startAuthenticatedRealtime(String(userId), version);
  },
  { immediate: true }
);

async function startAuthenticatedRealtime(userId: EntityId, version: number): Promise<void> {
  try {
    await pendingRealtimeStop;
    if (version !== realtimeLifecycleVersion || String(session.profile?.id) !== userId) {
      return;
    }

    await Promise.all([
      startWorkspaceRealtime(realtimeHandlers),
      notifications.loadUnread()
    ]);
  } catch (error) {
    if (version === realtimeLifecycleVersion) {
      workspace.error = error instanceof Error ? error.message : 'Realtime workspace updates are unavailable.';
    }
  }
}

onBeforeUnmount(() => {
  realtimeLifecycleVersion++;
  void stopWorkspaceRealtime();
});

const appShellClass = computed(() => {
  let value = 'app-shell';
  if (!session.isAuthenticated) {
    return `${value} unauthenticated`;
  }

  if (inSettings.value) {
    return `${value} settings-chrome`;
  }

  if (route.meta.chrome === 'changes') {
    return `${value} changes-chrome`;
  }

  if (sidebarHidden.value) {
    value += ' sidebar-hidden';
  }

  if (sidebarOpen.value) {
    value += ' sidebar-open';
  }

  return value;
});

function toggleSidebarHidden(): void {
  sidebarHidden.value = !sidebarHidden.value;
  if (sidebarHidden.value) {
    sidebarOpen.value = false;
  }
}

function toggleSidebarOpen(): void {
  sidebarOpen.value = !sidebarOpen.value;
  if (sidebarOpen.value) {
    sidebarHidden.value = false;
  }
}

provide(sidebarControlsKey, {
  sidebarHidden,
  sidebarOpen,
  toggleHidden: toggleSidebarHidden,
  toggleOpen: toggleSidebarOpen
});
</script>
